// src/pages/proyectos/components/FaseFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, Button, Input, Label, Textarea } from '@/components/ui';
import { useCrearFase, useActualizarFase } from '@/hooks/queries/useProyectos';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import type { ProyectoFase, ProyectoFaseCreate, ProyectoFaseUpdate } from '@/types';

const schema = z.object({
  nombre: z.string().min(1, 'Nombre requerido').max(100, 'Máximo 100 caracteres'),
  descripcion: z.string().optional(),
  orden: z.coerce.number().min(0).optional(),
  fecha_inicio_planeada: z.string().optional(),
  fecha_fin_planeada: z.string().optional(),
  es_hito: z.boolean().optional(),
  horas_estimadas: z.coerce.number().min(0).optional(),
  estado: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  proyectoId: string;
  fase?: ProyectoFase;
}

export function FaseFormModal({ open, onClose, proyectoId, fase }: Props) {
  const crearMutation = useCrearFase();
  const actualizarMutation = useActualizarFase();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      es_hito: false,
      orden: 0,
    },
  });

  const estado = watch('estado');

  useEffect(() => {
    if (fase) {
      reset({
        nombre: fase.nombre,
        descripcion: fase.descripcion ?? '',
        orden: fase.orden,
        fecha_inicio_planeada: fase.fecha_inicio_planeada ?? '',
        fecha_fin_planeada: fase.fecha_fin_planeada ?? '',
        es_hito: fase.es_hito,
        horas_estimadas: fase.horas_estimadas ?? undefined,
        estado: fase.estado,
      });
    } else {
      reset({
        nombre: '',
        descripcion: '',
        orden: 0,
        fecha_inicio_planeada: '',
        fecha_fin_planeada: '',
        es_hito: false,
        horas_estimadas: undefined,
        estado: undefined,
      });
    }
  }, [fase, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        ...data,
        fecha_inicio_planeada: data.fecha_inicio_planeada || undefined,
        fecha_fin_planeada: data.fecha_fin_planeada || undefined,
        horas_estimadas: data.horas_estimadas || undefined,
      };

      if (fase) {
        await actualizarMutation.mutateAsync({
          faseId: fase.id,
          proyectoId,
          data: payload as ProyectoFaseUpdate,
        });
      } else {
        await crearMutation.mutateAsync({
          proyectoId,
          data: payload as ProyectoFaseCreate,
        });
      }
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <h2 className="text-xl font-semibold mb-6">
          {fase ? 'Editar Fase' : 'Nueva Fase'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label>Nombre *</Label>
            <Input
              {...register('nombre')}
              placeholder="Nombre de la fase"
              className="mt-1"
            />
            {errors.nombre && (
              <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
            )}
          </div>

          <div>
            <Label>Descripción</Label>
            <Textarea
              {...register('descripcion')}
              placeholder="Descripción de la fase..."
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha inicio</Label>
              <Input
                type="date"
                {...register('fecha_inicio_planeada')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input
                type="date"
                {...register('fecha_fin_planeada')}
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Horas estimadas</Label>
              <Input
                type="number"
                {...register('horas_estimadas')}
                min={0}
                step={0.5}
                placeholder="0"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Orden</Label>
              <Input
                type="number"
                {...register('orden')}
                min={0}
                className="mt-1"
              />
            </div>
          </div>

          {fase && (
            <div>
              <Label>Estado</Label>
              <Select
                value={estado ?? 'PENDIENTE'}
                onValueChange={(value) => setValue('estado', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                  <SelectItem value="EN_PROGRESO">En Progreso</SelectItem>
                  <SelectItem value="COMPLETADA">Completada</SelectItem>
                  <SelectItem value="CANCELADA">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('es_hito')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Marcar como hito</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : fase ? 'Guardar cambios' : 'Crear fase'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
